import React, { useState, useEffect } from "react";
import { useToaster } from "../../contexts/ToasterContext";
import { FiEye, FiEyeOff, FiCopy, FiInfo, FiChevronDown } from "react-icons/fi";
import { STATIC_CONFIG_DATA, STATIC_STORAGE_ACCOUNTS, STATIC_VAULT_PROVIDER_ACCOUNTS, STATIC_FILE_EXTENSIONS, STATIC_VALIDATION_STATUS } from "./staticData";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import { FileText } from "lucide-react";
import { showFreeAssessmentToast } from "../../utils/freeAssessmentRedirect";
// Removed framer-motion to prevent shaking animations


// Shimmer Effect Components (static, no animation)
const ShimmerBox = ({ width, height, className = "" }) => (
  <div
    className={`bg-gray-200 rounded ${className}`}
    style={{
      width,
      height
    }}
  />
);

const ShimmerBar = ({ width, height, className = "" }) => (
  <div
    className={`bg-gray-200 rounded ${className}`}
    style={{
      width: width || '100%',
      height: height || '16px'
    }}
  />
);

const ShimmerCard = () => (
  <Card className="border shadow-sm w-full">
    <CardHeader className="flex flex-row items-center justify-between">
      <div className="flex items-center gap-3">
        <ShimmerBox width="32px" height="32px" />
        <ShimmerBar width="128px" height="24px" />
      </div>
      <div className="flex gap-2">
        <ShimmerBar width="64px" height="32px" />
        <ShimmerBox width="32px" height="32px" />
      </div>
    </CardHeader>
    <CardContent className="p-0">
      <div className="grid grid-cols-2 bg-gray-100 px-4 py-2 gap-4">
        <ShimmerBar width="100%" height="16px" />
        <ShimmerBar width="100%" height="16px" />
      </div>
      <div className="space-y-3 p-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="grid grid-cols-2 gap-4">
            <ShimmerBar width="100%" height="16px" />
            <ShimmerBar width="100%" height="16px" />
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

// File Extension Input Component
const FileExtensionInput = ({ value, onChange, className }) => {
  const [inputValue, setInputValue] = useState("");

  // Common file extensions for quick selection
  const commonExtensions = [
    ".js",
    ".py",
    ".go",
    ".ts",
    ".java",
    ".cpp",
    ".php",
    ".rb",
    ".swift",
    ".kt",
    ".rs",
    ".tsx",
    ".jsx",
    ".vue",
    ".json",
    ".yaml",
    ".yml",
    ".xml",
    ".sql",
  ];

  const addExtension = () => {
    if (!inputValue.trim()) return;

    const newExt = inputValue.trim();
    // Normalize extension to have leading dot
    const normalizedExt = newExt.startsWith(".") ? newExt : `.${newExt}`;

    if (!value.includes(normalizedExt)) {
      onChange([...value, normalizedExt]);
    }
    setInputValue("");
  };

  const toggleExtension = (ext) => {
    if (value.includes(ext)) {
      onChange(value.filter((e) => e !== ext));
    } else {
      onChange([...value, ext]);
    }
  };

  const removeExtension = (index) => {
    const newExtensions = value.filter((_, i) => i !== index);
    onChange(newExtensions);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addExtension();
    }
  };

  return (
    <div className={className}>
      <div className="flex flex-col gap-3">
        {/* Current Extensions Display */}
        {value && value.length > 0 && (
          <div className="bg-gray-50 border rounded-lg p-3">
            <div className="text-xs font-medium text-gray-700 mb-2">
              Selected Extensions ({value.length})
            </div>
            <div className="flex flex-wrap gap-1">
              {value.map((ext, index) => (
                <Badge
                  key={index}
                  variant="default"
                  className="font-medium text-xs flex items-center gap-1 bg-blue-100 text-blue-800 hover:bg-blue-200"
                >
                  {ext}
                  <button
                    onClick={() => removeExtension(index)}
                    className="ml-1 hover:bg-blue-300 rounded-full p-0.5 transition-colors"
                    type="button"
                  >
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Common Extensions Checkboxes */}
        <div className="border rounded-lg p-3">
          <div className="text-xs font-medium text-gray-700 mb-2">
            Common Extensions
          </div>
          <div className="grid grid-cols-6 gap-2">
            {commonExtensions.map((ext) => {
              const isSelected = value.includes(ext);
              return (
                <label
                  key={ext}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleExtension(ext)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-xs font-mono text-gray-700">{ext}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Custom Extension Input */}
        <div className="border rounded-lg p-3">
          <div className="text-xs font-medium text-gray-700 mb-2">
            Add Custom Extension
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="e.g., .jsx, scala, .rs"
              className="flex-1 h-9 text-sm"
            />
            <Button
              type="button"
              onClick={addExtension}
              disabled={!inputValue.trim()}
              size="sm"
              className="h-9 px-3 text-xs"
            >
              Add
            </Button>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Extensions can be added with or without the leading dot
          </div>
        </div>
      </div>
    </div>
  );
};

// Provider configurations for each category
const categoryProviders = {
  vcs: {
    title: "Choose Your Preferred Git Provider",
    providers: {
      github: {
        name: "GitHub",
        icon: "/settingspage/github.svg.png",
      },
      gitlab: {
        name: "GitLab",
        icon: "/gitlab.svg",
      },
      gitea: {
        name: "Gitea",
        icon: "/inte/gitea.png",
      },
      bitbucket: {
        name: "Bitbucket",
        icon: "/bitbucket.png",
      },
    },
  },
  communication: {
    title: "Choose Your Preferred Communication Service",
    providers: {
      slack: {
        name: "Slack",
        icon: "/settingspage/slack.png",
      },
      teams: {
        name: "Microsoft Teams",
        icon: "/settingspage/msteams.webp",
      },
    },
  },
  aiModel: {
    title: "Choose Your Preferred AI Model Provider",
    providers: {
      gemini: {
        name: "Gemini",
        icon: "/ai-logos/gemini.png",
      },
      openai: {
        name: "OpenAI",
        icon: "/ai-logos/openai.webp",
      },
      anthropic: {
        name: "Anthropic",
        icon: "/ai-logos/anthropic.webp",
      },
      groq: {
        name: "Groq",
        icon: "/ai-logos/groq.png",
      },
      deepseek: {
        name: "DeepSeek",
        icon: "/ai-logos/deepseek.png",
      },
      llama: {
        name: "Llama",
        icon: "/ai-logos/llama.jpg",
      },
      qwen: {
        name: "Qwen",
        icon: "/ai-logos/qwen.png",
      },
    },
  },
  vault: {
    title: "Choose Your Preferred Vault Provider",
    providers: {
      hashicorp: {
        name: "HashiCorp Vault",
        icon: "/ai-logos/hashicorp.svg",
      },
        gcp_secret_manager: {
          name: "Google Cloud Secret Manager",
          icon: "/inte/GCP.png",
        },
      aws_parameter_store: {
        name: "AWS Parameter Store",
        icon: "/settingspage/aws.svg.png",
      },
    },
  },
  ticketing: {
    title: "Choose Your Preferred Ticketing Service",
    providers: {
      jira: {
        name: "Jira",
        icon: "/settingspage/jira.png",
      },
      freshservice: {
        name: "Freshservice",
        icon: "/settingspage/freshworks.jpg",
      },
    },
  },
};

// Category configuration for sidebar
const categories = {
  cloudStorage: {
    title: "Cloud & Storage",
    icon: "☁️",
    services: {
      aws: {
        name: "Amazon Web Service",
        icon: "/settingspage/aws.svg.png",
        fields: ["aws_access_key_id", "aws_secret_access_key", "aws_region"],
      },
      azure: {
        name: "Azure",
        icon: "/inte/Azure.png",
        fields: [
          "azure_tenant_id",
          "azure_client_id",
          "azure_client_secret",
          "azure_storage_account",
        ],
      },
      gcp: {
        name: "Google Cloud Platform",
        icon: "/inte/GCP.png",
        fields: [
          "gcp_project_id",
          "gcp_private_key_id",
          "gcp_private_key",
          "gcp_client_email",
          "gcp_client_id",
          "gcp_auth_uri",
          "gcp_token_uri",
          "gcp_auth_provider_x509_cert_url",
          "gcp_client_x509_cert_url",
          "gcp_universe_domain",
        ],
      },
    },
  },
  vcs: {
    title: "Version Control System",
    icon: "🔗",
    services: {
      github: {
        name: "GitHub",
        icon: "/settingspage/github.svg.png",
        fields: ["github_pat", "github_webhook_secret"],
      },
      gitlab: {
        name: "GitLab",
        icon: "/gitlab.svg",
        fields: ["gitlab_pat"],
      },
      gitea: {
        name: "Gitea",
        icon: "/inte/gitea.png",
        fields: ["gitea_pat", "gitea_instance_url"],
      },
      bitbucket: {
        name: "Bitbucket",
        icon: "/bitbucket.png",
        fields: ["bitbucket_pat", "bitbucket_workspace"],
      },
    },
  },
  communication: {
    title: "Communication Services",
    icon: "💬",
    services: {
      slack: {
        name: "Slack",
        icon: "/settingspage/slack.png",
        fields: [
          "slack_bot_token",
          "slack_bot_user_id",
          "slack_user_token",
          "slack_alert_channel",
          "slack_signing_secret",
          "slack_blacklisted_channels",
          "slack_events_enabled",
          "slack_history_enabled",
        ],
      },
      teams: {
        name: "Microsoft Teams",
        icon: "/settingspage/msteams.webp",
        fields: [
          "teams_tenant_id",
          "teams_app_client_id",
          "teams_app_client_secret",
          "teams_webhook_url",
          "teams_webhook_secret",
          "teams_security_team_id",
          "teams_security_channel_id",
          "teams_redirect_uri",
          "teams_public_url",
          "teams_subscription_expiry_days",
        ],
      },
    },
  },
  vault: {
    title: "Vaults",
    icon: "🔐",
    services: {
      hashicorp: {
        name: "HashiCorp Vault",
        icon: "/ai-logos/hashicorp.svg",
        fields: [
          "hashicorp_vault_url",
          "hashicorp_vault_token",
          "hashicorp_vault_mount_path",
        ],
      },
      aws_parameter_store: {
        name: "AWS Parameter Store",
        icon: "/settingspage/aws.svg.png",
        fields: [], // Uses AWS credentials from cloud storage
      },
    },
  },
  aiModel: {
    title: "AI Models (Remediation)",
    icon: "🤖",
    services: {
      gemini: {
        name: "Gemini",
        icon: "/ai-logos/gemini.png",
        fields: ["gemini_api_key", "llm_model_id"],
      },
      openai: {
        name: "OpenAI",
        icon: "/ai-logos/openai.webp",
        fields: ["openai_model_id", "openai_api_key", "openai_base_url"],
      },
      anthropic: {
        name: "Anthropic",
        icon: "/ai-logos/anthropic.webp",
        fields: ["anthropic_api_key", "claude_model_id"],
      },
      groq: {
        name: "Groq",
        icon: "/ai-logos/groq.png",
        fields: ["groq_api_key", "groq_model_id"],
      },
      deepseek: {
        name: "DeepSeek",
        icon: "/ai-logos/deepseek.png",
        fields: ["deepseek_api_key", "deepseek_model_id", "deepseek_base_url"],
      },
      llama: {
        name: "Llama",
        icon: "/ai-logos/llama.jpg",
        fields: ["llama_api_key", "llama_model_id"],
      },
      qwen: {
        name: "Qwen",
        icon: "/ai-logos/qwen.png",
        fields: ["qwen_auth_token", "qwen_cookie", "qwen_model_id"],
      },
    },
  },
  ticketing: {
    title: "Ticketing Service",
    icon: "🎫",
    services: {
      jira: {
        name: "Jira",
        icon: "/settingspage/jira.png",
        fields: [
          "jira_url",
          "jira_username",
          "jira_api_token",
          "jira_project_key",
          "auto_ticket_creation",
        ],
      },
      freshservice: {
        name: "Freshservice",
        icon: "/settingspage/freshworks.jpg",
        fields: [
          "freshservice_email_id",
          "freshservice_url",
          "freshservice_api_key",
          "auto_ticket_creation",
        ],
      },
    },
  },
};

// Provider Selector Component (supports an optional vault toggle next to AWS Parameter Store)
const ProviderSelector = ({
  categoryKey,
  currentProvider,
  onProviderChange,
  // vault-specific props
  vaultAutoInsert = false,
  onToggleVaultAutoInsert = null,
}) => {
  const categoryConfig = categoryProviders[categoryKey];

  if (!categoryConfig) return null;

  return (
    <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
      <h3 className="text-sm font-medium text-gray-700 mb-3">
        {categoryConfig.title}
      </h3>
      <div className="flex flex-wrap gap-3">
        {Object.entries(categoryConfig.providers).map(([providerKey, provider]) => {
          const isSelected = currentProvider === providerKey;
          return (
            <div key={providerKey} className="flex items-center gap-2">
              <button
                onClick={() => onProviderChange(providerKey)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all duration-200 ${
                  isSelected
                    ? "border-[#44087D] bg-[#44087D]/10 text-[#44087D] shadow-sm"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm text-gray-700"
                }`}
              >
                <img src={provider.icon} alt={provider.name} className="w-5 h-5 object-contain" />
                <span className="text-sm font-medium">{provider.name}</span>
                {isSelected && <div className="w-2 h-2 bg-[#44087D] rounded-full ml-1"></div>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ConfigManagement = ({
  selectedCategory = null,
  showCreateAccountDialog: propShowCreateAccountDialog,
  setShowCreateAccountDialog: propSetShowCreateAccountDialog
}) => {
  const { showToast } = useToaster();
  const [config, setConfig] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [preferredTicketingService, setPreferredTicketingService] =
    useState("jira");
  const [errorMsg, setErrorMsg] = useState("");
  const [expandedCard, setExpandedCard] = useState(null); // Default to none expanded
  const [editingSectionTitle, setEditingSectionTitle] = useState(null);
  const [validationStatus, setValidationStatus] = useState({
    github: "valid",
    gitlab: "valid",
    bitbucket: "valid",
    gitea: "valid",
    aws: "valid",
    azure: "valid",
    gcp: "valid",
    hashicorp: "valid",
    gemini: "valid",
    slack: "valid",
    jira: "valid",
    freshservice: "valid",
    openai: "valid",
    anthropic: "valid",
    groq: "valid",
    deepseek: "valid",
    llama: "valid",
    qwen: "valid",
    teams: "valid",
  });
  const [preferredCommunicationService, setPreferredCommunicationService] =
    useState("slack");
  const [preferredGitProvider, setPreferredGitProvider] = useState("github");
  const [preferredLlmProvider, setPreferredLlmProvider] = useState("google");
  const [vaultProvider, setVaultProvider] = useState("hashicorp");
  const [autoInsertToVault, setAutoInsertToVault] = useState(false);
  // Only used for vault category
  const [teamsOAuthLoading, setTeamsOAuthLoading] = useState(false);
  const [fileExtensions, setFileExtensions] = useState([]);
  // Removed fileExtensionsLoading - not needed for static data
  // Storage accounts fetched from backend
  const [storageAccounts, setStorageAccounts] = useState([]);
  const [loadingStorageAccounts, setLoadingStorageAccounts] = useState(false);
  // Provider-specific vault accounts (AWS Parameter Store / GCP Secret Manager)
  const [vaultProviderAccounts, setVaultProviderAccounts] = useState([]);
  const [loadingVaultProviderAccounts, setLoadingVaultProviderAccounts] = useState(false);
  const [selectedVaultAccount, setSelectedVaultAccount] = useState(null);
  const [internalShowCreateAccountDialog, setInternalShowCreateAccountDialog] = useState(false);

  // Use props if provided, otherwise use internal state
  const showCreateAccountDialog = propShowCreateAccountDialog !== undefined
    ? propShowCreateAccountDialog
    : internalShowCreateAccountDialog;
  const setShowCreateAccountDialog = propSetShowCreateAccountDialog || setInternalShowCreateAccountDialog;
  const [createAccountProvider, setCreateAccountProvider] = useState("aws");
  const [createAccountState, setCreateAccountState] = useState({
    name: "",
    aws_access_key_id: "",
    aws_secret_access_key: "",
    aws_region: "",
    // Azure fields
    azure_tenant_id: "",
    azure_client_id: "",
    azure_client_secret: "",
    azure_storage_account: "",
    // GCP fields (JSON + optional project id)
    gcp_project_id: "",
    gcp_service_account_json: "",
  });

  const [editingAccountId, setEditingAccountId] = useState(null);
  const [showLegacyEditor, setShowLegacyEditor] = useState(false);
  const [legacyEditorSection, setLegacyEditorSection] = useState(null);
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteAccountTarget, setDeleteAccountTarget] = useState(null);
  // (view-only) we populate `config` with fetched account fields when user opens the chevron

  // Fetch storage accounts from static data
  const fetchStorageAccounts = () => {
    setLoadingStorageAccounts(true);
    try {
      setStorageAccounts(STATIC_STORAGE_ACCOUNTS);
    } catch (err) {
      console.error("Error loading storage accounts:", err);
      showToast("Failed to load storage accounts", "error");
    } finally {
      setLoadingStorageAccounts(false);
    }
  };

  // Fetch accounts for the currently selected vault provider from static data
  const fetchVaultProviderAccounts = (providerKey) => {
    setLoadingVaultProviderAccounts(true);
    try {
      // Only support AWS Parameter Store and GCP Secret Manager for provider listing
      if (providerKey === "aws_parameter_store") {
        setVaultProviderAccounts(STATIC_VAULT_PROVIDER_ACCOUNTS.aws_parameter_store || []);
      } else if (providerKey === "gcp_secret_manager") {
        setVaultProviderAccounts(STATIC_VAULT_PROVIDER_ACCOUNTS.gcp_secret_manager || []);
      } else {
        // Clear any previous list for unsupported providers
        setVaultProviderAccounts([]);
      }
    } catch (err) {
      console.error("Error loading provider accounts:", err);
      showToast("Failed to load provider accounts", "error");
      setVaultProviderAccounts([]);
    } finally {
      setLoadingVaultProviderAccounts(false);
    }
  };

  // Ensure backward compatibility: if UI provides bitbucket_app_password, also send bitbucket_pat
  const ensureBitbucketPat = (payload) => {
    try {
      if (!payload) return;
      // If a new app password field is present, copy it to bitbucket_pat for backend compatibility
      if (payload.bitbucket_app_password) {
        payload.bitbucket_pat = payload.bitbucket_app_password;
      }
    } catch (e) {
      // ignore
    }
  };

  // Helper: parse a raw input (string or object) and return a normalized service account object.
  // Accepts shapes like:
  // - raw service account JSON
  // - { project_id, service_account: { ... } }
  // - { ProjectID: "...", ServiceAccountJSON: "{...}" }
  // - { gcp: { service_account: { ... } } }
  const parseServiceAccountFromRaw = (rawInput) => {
    if (!rawInput) return null;

    let parsed = null;
    // If input is already an object, use it directly
    if (typeof rawInput === "object" && rawInput !== null) parsed = rawInput;
    else if (typeof rawInput === "string") {
      try {
        parsed = JSON.parse(rawInput || "{}");
      } catch (e) {
        // Not valid JSON string
        return null;
      }
    } else {
      return null;
    }

    // Create a map of lowercase keys -> original key for case-insensitive lookup
    const keyMap = Object.keys(parsed || {}).reduce((acc, k) => {
      acc[k.toLowerCase()] = k;
      return acc;
    }, {});

    // If there's a ServiceAccountJSON string wrapper, parse it
    if (keyMap["serviceaccountjson"]) {
      const orig = keyMap["serviceaccountjson"];
      const val = parsed[orig];
      if (typeof val === "string") {
        try {
          const inner = JSON.parse(val);
          return inner;
        } catch (e) {
          // fallthrough
        }
      }
    }

    // If nested gcp.service_account exists
    if (keyMap["gcp"]) {
      const gcpObj = parsed[keyMap["gcp"]];
      if (gcpObj && typeof gcpObj === "object") {
        if (gcpObj.service_account) return gcpObj.service_account;
      }
    }

    // If a service_account key exists (case-insensitive)
    if (keyMap["service_account"]) return parsed[keyMap["service_account"]];
    if (keyMap["serviceaccount"]) return parsed[keyMap["serviceaccount"]];

    // Otherwise consider parsed itself as the service-account JSON
    return parsed;
  };

  // Open a read-only view for a storage account (used by tile click and chevron)
  const handleOpenAccountView = async (acc) => {
    const accountId = acc.id || acc.account_id;
    try {
      // Clear any edit markers to ensure view-only mode
      setEditingSectionTitle(null);
      setEditingAccountId(null);

      // Static: Use account from storageAccounts state
      const foundAccount = storageAccounts.find(a => (a.id || a.account_id) === accountId);
      if (!foundAccount) {
        showToast("Account not found", "error");
        return;
      }

      // Normalize response shape: support new { Account, AWS, Azure, GCP } and old shapes.
      const rawResp = foundAccount || null;
      let account = null;
      if (rawResp && rawResp.Account) {
        account = rawResp.Account || null;
        account.aws = rawResp.AWS || null;
        account.azure = rawResp.Azure || null;
        account.gcp = rawResp.GCP || null;
      } else {
        account = rawResp?.account || rawResp || null;
      }

      try {
        if (account && account.body && typeof account.body.raw === "string") {
          const parsed = JSON.parse(account.body.raw);
          account = { ...account, ...parsed };
        }
      } catch (parseErr) {
        console.warn("Could not parse account.body.raw", parseErr);
      }

      if (account) {
        const provider = (account.provider || "").toLowerCase();

        if (provider.includes("s3") || account.aws) {
          const awsObj = account.aws || account.AWS || {};
          setConfig((prev) => ({
            ...prev,
            aws_access_key_id:
              awsObj.access_key_id ||
              awsObj.accessKeyId ||
              awsObj.AccessKeyID ||
              awsObj.AccessKey ||
              "",
            aws_secret_access_key:
              awsObj.secret_access_key ||
              awsObj.secretAccessKey ||
              awsObj.SecretAccessKey ||
              awsObj.SecretKey ||
              "",
            aws_region: awsObj.region || account.aws_region || "",
          }));

          const section = {
            title: "Amazon Web Service",
            icon: "/settingspage/aws.svg.png",
            fields: [
              "aws_access_key_id",
              "aws_secret_access_key",
              "aws_region",
            ],
          };
          setLegacyEditorSection(section);
        } else if (provider === "azure" || account.azure) {
          const azureObj = account.azure || {};
          setConfig((prev) => ({
            ...prev,
            azure_tenant_id:
              azureObj.tenant_id ||
              azureObj.tenantId ||
              azureObj.TenantID ||
              account.azure_tenant_id ||
              "",
            azure_client_id:
              azureObj.client_id ||
              azureObj.clientId ||
              azureObj.ClientID ||
              account.azure_client_id ||
              "",
            azure_client_secret:
              azureObj.client_secret ||
              azureObj.clientSecret ||
              azureObj.ClientSecret ||
              account.azure_client_secret ||
              "",
            azure_storage_account:
              azureObj.storage_account ||
              azureObj.storageAccount ||
              azureObj.StorageAccount ||
              account.azure_storage_account ||
              "",
          }));

          const section = {
            title: "Azure",
            icon: "/inte/Azure.png",
            fields: [
              "azure_tenant_id",
              "azure_client_id",
              "azure_client_secret",
              "azure_storage_account",
            ],
          };
          setLegacyEditorSection(section);
        } else if (provider === "gcp" || account.gcp) {
          // For the chevron/view dropdown we only want to show project_id and the
          // full service account JSON (read-only view). Keep the heavy JSON
          // under a single field so the ServiceCard will render it compactly.
          const saObj =
            (account.gcp && account.gcp.service_account) ||
            account.gcp_service_account ||
            account.gcp ||
            null;

          // Pretty-print the service account JSON for display
          const prettyJson = saObj ? JSON.stringify(saObj, null, 2) : "";

          // Populate config with just the two view-only fields
          setConfig((prev) => ({
            ...prev,
            gcp_project_id:
              (account.gcp && account.gcp.project_id) ||
              account.gcp_project_id ||
              "",
            gcp_service_account_json: prettyJson,
          }));

          const section = {
            title: "Google Cloud Platform",
            icon: "/inte/GCP.png",
            fields: ["gcp_project_id", "gcp_service_account_json"],
          };
          setLegacyEditorSection(section);
        } else {
          setCreateAccountProvider(provider || "aws");
          setCreateAccountState({ name: account.name || "" });
          setShowCreateAccountDialog(true);
        }
      }

      setExpandedAccountId(accountId === expandedAccountId ? null : accountId);
    } catch (err) {
      console.error("Error fetching storage account:", err);
      const msg =
        err?.response?.data?.message || "Failed to fetch storage account";
      showToast(msg, "error");
    }
  };

  // Open edit mode for a storage account by fetching latest details from static data
  const handleOpenAccountEdit = async (acc) => {
    const accountId = acc.id || acc.account_id;
    try {
      // Static: Use account from storageAccounts state
      const foundAccount = storageAccounts.find(a => (a.id || a.account_id) === accountId);
      if (!foundAccount) {
        showToast("Account not found", "error");
        return;
      }

      // Normalize response like the view handler: support {Account,AWS,Azure,GCP} and old shapes
      const rawResp = foundAccount || null;
      let account = null;
      let awsObj = null;
      let azureObj = null;
      let gcpObj = null;

      if (rawResp && rawResp.Account) {
        account = rawResp.Account || null;
        awsObj = rawResp.AWS || null;
        azureObj = rawResp.Azure || null;
        gcpObj = rawResp.GCP || null;
      } else {
        account = rawResp?.account || rawResp || null;
        awsObj =
          account && (account.aws || account.AWS)
            ? account.aws || account.AWS
            : null;
        azureObj =
          account && (account.azure || account.Azure)
            ? account.azure || account.Azure
            : null;
        gcpObj =
          account && (account.gcp || account.GCP)
            ? account.gcp || account.GCP
            : null;
      }

      try {
        if (account && account.body && typeof account.body.raw === "string") {
          const parsed = JSON.parse(account.body.raw);
          account = { ...account, ...parsed };
        }
      } catch (parseErr) {
        console.warn("Could not parse account.body.raw", parseErr);
      }

      if (account) {
        const provider = (account.provider || account.provider_type || "")
          .toString()
          .toLowerCase();

        if (provider.includes("s3") || awsObj) {
          const a = awsObj || account.aws || account.AWS || {};
          console.debug("[edit] Storage account AWS object:", a);
          setConfig((prev) => ({
            ...prev,
            name: account.name || "",
            aws_access_key_id:
              a.access_key_id ||
              a.accessKeyId ||
              a.AccessKeyID ||
              a.AccessKey ||
              "",
            aws_secret_access_key:
              a.secret_access_key ||
              a.secretAccessKey ||
              a.SecretAccessKey ||
              a.SecretKey ||
              "",
            aws_region: a.region || account.aws_region || "",
          }));

          setShowPasswords((prev) => ({
            ...prev,
            aws_access_key_id: true,
            aws_secret_access_key: true,
          }));

          const section = {
            title: "Amazon Web Service",
            icon: "/settingspage/aws.svg.png",
            fields: [
              "name",
              "aws_access_key_id",
              "aws_secret_access_key",
              "aws_region",
            ],
          };
          setLegacyEditorSection(section);
          setEditingSectionTitle(section.title);
          setEditingAccountId(accountId);
          setExpandedAccountId(accountId);
        } else if (provider === "azure" || azureObj) {
          const az = azureObj || account.azure || account.Azure || {};
          console.debug("[edit] Storage account Azure object:", az);
          setConfig((prev) => ({
            ...prev,
            name: account.name || "",
            azure_tenant_id:
              az.tenant_id ||
              az.tenantId ||
              az.TenantID ||
              account.azure_tenant_id ||
              "",
            azure_client_id:
              az.client_id ||
              az.clientId ||
              az.ClientID ||
              account.azure_client_id ||
              "",
            azure_client_secret:
              az.client_secret ||
              az.clientSecret ||
              az.ClientSecret ||
              account.azure_client_secret ||
              "",
            azure_storage_account:
              az.storage_account ||
              az.storageAccount ||
              az.StorageAccount ||
              account.azure_storage_account ||
              "",
          }));

          setShowPasswords((prev) => ({
            ...prev,
            azure_client_secret: true,
          }));

          const section = {
            title: "Azure",
            icon: "/inte/Azure.png",
            fields: [
              "name",
              "azure_tenant_id",
              "azure_client_id",
              "azure_client_secret",
              "azure_storage_account",
            ],
          };
          setLegacyEditorSection(section);
          setEditingSectionTitle(section.title);
          setEditingAccountId(accountId);
          setExpandedAccountId(accountId);
        } else if (provider === "gcp" || gcpObj) {
          const g = gcpObj || account.gcp || account.GCP || {};
          const sa = g.service_account || g.serviceAccount || g || null;
          const prettyJson = sa
            ? typeof sa === "string"
              ? sa
              : JSON.stringify(sa, null, 2)
            : "";

          setConfig((prev) => ({
            ...prev,
            name: account.name || "",
            gcp_project_id:
              g.project_id || g.projectId || account.gcp_project_id || "",
            gcp_service_account_json: prettyJson,
          }));

          const section = {
            title: "Google Cloud Platform",
            icon: "/inte/GCP.png",
            fields: ["name", "gcp_project_id", "gcp_service_account_json"],
          };
          setLegacyEditorSection(section);
          setEditingSectionTitle(section.title);
          setEditingAccountId(accountId);
          setExpandedAccountId(accountId);
        } else {
          // Unknown provider: open create dialog prefilled with name
          setCreateAccountProvider(provider || "aws");
          setCreateAccountState({ name: account.name || "" });
          setShowCreateAccountDialog(true);
          setEditingAccountId(accountId);
        }
      }
    } catch (err) {
      console.error("Error fetching storage account for edit:", err);
      const msg =
        err?.response?.data?.message || "Failed to fetch storage account";
      showToast(msg, "error");
    }
  };

  // Toggle expansion for a ServiceCard
  const toggleCardExpansion = (title) => {
    setExpandedCard((prev) => (prev === title ? null : title));
  };

  // Prompt deletion (opens dialog)
  const promptDeleteAccount = (acc) => {
    setDeleteAccountTarget(acc);
    setShowDeleteDialog(true);
  };

  // Perform delete of the selected account
  const performDeleteAccount = async () => {
    const target = deleteAccountTarget;
    if (!target) return;
    // Show toast for USP feature
    showFreeAssessmentToast();
    setShowDeleteDialog(false);
    setDeleteAccountTarget(null);
    return;
  };

  // Create a storage account (POST) based on the selected provider and form state
  const createStorageAccount = async () => {
    // Show toast for USP feature instead of creating account
    showFreeAssessmentToast();
    setShowCreateAccountDialog(false);
    return;
  }
  // Helper: return current provider for a given category
  const getCurrentProvider = (categoryKey) => {
    switch (categoryKey) {
      case "vcs":
        return preferredGitProvider;
      case "communication":
        return preferredCommunicationService;
      case "aiModel":
        return preferredLlmProvider;
      case "vault":
        return vaultProvider;
      case "ticketing":
        return preferredTicketingService;
      default:
        return null;
    }
  };

  // Handler for provider selector changes
  const handleProviderChange = async (categoryKey, providerKey) => {
    switch (categoryKey) {
      case "vcs":
        setPreferredGitProvider(providerKey);
        break;
      case "communication":
        setPreferredCommunicationService(providerKey);
        break;
      case "aiModel":
        setPreferredLlmProvider(providerKey);
        break;
      case "vault":
        // When vault provider changes in the selector, persist the preference
        // to the backend and show a toast. Use the existing handler which
        // performs the PUT /api/config update.
        await handleVaultProviderChange(providerKey);
        break;
      case "ticketing":
        setPreferredTicketingService(providerKey);
        break;
      default:
        break;
    }
  };

  // Function to handle AI service configuration change
  const handleAiServiceChange = async (newProvider) => {
    // Show toast for USP feature
    showFreeAssessmentToast();
    return;
  };

  // Function to handle vault provider change
  const handleVaultProviderChange = async (newProvider) => {
    // Show toast for USP feature
    showFreeAssessmentToast();
    setVaultProvider(newProvider);
    return;
  };

  // Persist selected vault account and provider into /api/config
  const selectVaultAccount = async (acc, providerKey) => {
    if (!acc || !providerKey) return;
    // Show toast for USP feature
    showFreeAssessmentToast();
    return;
  };

  // Toggle and persist auto-insert-to-vault preference
  const handleToggleAutoInsertToVault = async (newVal) => {
    // Show toast for USP feature
    showFreeAssessmentToast();
    return;
  };

  // When vaultProvider state changes (including initial load), fetch provider accounts
  useEffect(() => {
    if (
      vaultProvider === "aws_parameter_store" ||
      vaultProvider === "gcp_secret_manager"
    ) {
      fetchVaultProviderAccounts(vaultProvider);
    } else {
      setVaultProviderAccounts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultProvider]);

  // Function to handle preferred ticketing service change
  const handlePreferredServiceChange = async (newService) => {
    // Show toast for USP feature
    showFreeAssessmentToast();
    return;
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchConfig = () => {
      try {
        // Use static configuration data
        const data = STATIC_CONFIG_DATA;

        const normalized = {
          github_pat: data.github_pat || "",
          gitlab_pat: data.gitlab_pat || "",
          bitbucket_pat: data.bitbucket_pat || "",
          gitea_pat: data.gitea_pat || "",
          gitea_instance_url: data.gitea_instance_url || "",
          bitbucket_workspace: data.bitbucket_workspace || "",
          jira_url: data.jira_url || "",
          jira_username: data.jira_username || "",
          jira_api_token: data.jira_api_token || "",
          jira_project_key: data.jira_project_key || "",
          aws_access_key_id: data.aws_access_key_id || "",
          aws_secret_access_key: data.aws_secret_access_key || "",
          aws_region: data.aws_region || "",
          azure_tenant_id: data.azure_tenant_id || "",
          azure_client_id: data.azure_client_id || "",
          azure_client_secret: data.azure_client_secret || "",
          azure_storage_account: data.azure_storage_account || "",
          gcp_project_id: data?.project_id || "",
          gcp_private_key_id: data?.private_key_id || "",
          gcp_private_key: data?.private_key || "",
          gcp_client_email: data?.client_email || "",
          gcp_client_id: data?.client_id || "",
          gcp_auth_uri: data?.auth_uri || "",
          gcp_token_uri: data.gcp_service_account?.token_uri || "",
          gcp_auth_provider_x509_cert_url:
            data.gcp_service_account?.auth_provider_x509_cert_url || "",
          gcp_client_x509_cert_url:
            data.gcp_service_account?.client_x509_cert_url || "",
          gcp_universe_domain: data.gcp_service_account?.universe_domain || "",
          auto_insert_to_vault: data.auto_insert_to_vault || data.autoInsertToVault || false,
          slack_bot_token: data.slack_bot_token || "",
          slack_bot_user_id: data.slack_bot_user_id || "",
          slack_user_token: data.slack_user_token || "",
          slack_alert_channel: data.slack_alert_channel || "",
          slack_signing_secret: data.slack_signing_secret || "",
          slack_blacklisted_channels: data.slack_blacklisted_channels || "",
          slack_events_enabled:
            data.slack_events_enabled !== undefined
              ? data.slack_events_enabled
              : false,
          slack_history_enabled:
            data.slack_history_enabled !== undefined
              ? data.slack_history_enabled
              : false,
          freshservice_email_id: data.freshservice_email_id || "",
          freshservice_url: data.freshservice_url || "",
          freshservice_api_key: data.freshservice_api_key || "",
          teams_tenant_id: data.teams_tenant_id || "",
          teams_app_client_id: data.teams_app_client_id || "",
          teams_app_client_secret: data.teams_app_client_secret || "",
          teams_webhook_url: data.teams_webhook_url || "",
          teams_webhook_secret: data.teams_webhook_secret || "",
          teams_security_team_id: data.teams_security_team_id || "",
          teams_security_channel_id: data.teams_security_channel_id || "",
          teams_redirect_uri: data.teams_redirect_uri || "",
          teams_public_url: data.teams_public_url || "",
          teams_subscription_expiry_days:
            data.teams_subscription_expiry_days || "",
          // AI Service fields - using the same field names as API
          gemini_api_key: data.gemini_api_key || "",
          llm_model_id: data.llm_model_id || "",
          openai_model_id: data.openai_model_id || "",
          openai_api_key: data.openai_api_key || "",
          openai_base_url: data.openai_base_url || "",
          anthropic_api_key: data.anthropic_api_key || "",
          claude_model_id: data.claude_model_id || "",
          groq_api_key: data.groq_api_key || "",
          groq_model_id: data.groq_model_id || "",
          deepseek_api_key: data.deepseek_api_key || "",
          deepseek_model_id: data.deepseek_model_id || "",
          deepseek_base_url: data.deepseek_base_url || "",
          llama_api_key: data.llama_api_key || "",
          llama_model_id: data.llama_model_id || "",
          qwen_auth_token: data.qwen_auth_token || "",
          qwen_cookie: data.qwen_cookie || "",
          qwen_model_id: data.qwen_model_id || "",
          hashicorp_vault_url: data.hashicorp_vault_url || "",
          hashicorp_vault_token: data.hashicorp_vault_token || "",
          hashicorp_vault_mount_path: data.hashicorp_vault_mount_path || "",
          auto_ticket_creation:
            data.auto_ticket_creation !== undefined
              ? data.auto_ticket_creation
              : false,
          github_webhook_secret: data.github_webhook_secret || "",
        };

        if (!isCancelled) {
          setConfig(normalized);
          // restore auto-insert setting
          setAutoInsertToVault(!!normalized.auto_insert_to_vault);
          setHasFailed(false);

          // Use static preferences
          const preferredTool = "jira";
          setPreferredTicketingService(preferredTool);
          setPreferredCommunicationService("slack");
          const preferredProvider = "github";
          setPreferredGitProvider(preferredProvider);
          const preferredLlmProvider = "gemini";
          setPreferredLlmProvider(preferredLlmProvider);
          const vaultPref = "hashicorp";
          setVaultProvider(vaultPref);
        }
      } catch (err) {
        console.error("Error loading config:", err);
        const backendMessage = err?.message || "Failed to load configuration";
        if (!isCancelled) {
          setHasFailed(true);
          setErrorMsg(backendMessage);
        }
        showToast(backendMessage, "error");
      } finally {
        if (!isCancelled) {
          setLoading(false);
          setInitialDataLoaded(true);
        }
      }
    };

    const fetchFileExtensions = () => {
      if (!isCancelled) {
        setFileExtensions(STATIC_FILE_EXTENSIONS);
      }
    };

    if (!hasFailed) {
      fetchConfig();
      fetchFileExtensions();
      fetchStorageAccounts();
    }

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line
  }, [hasFailed]);
  // Validation helpers - always set to valid
  const validateGithubPat = async (tokenToValidate) => {
    setValidationStatus((s) => ({ ...s, github: "valid" }));
  };

  const validateAwsCredentials = async (
    accessKeyId,
    secretAccessKey,
    region
  ) => {
    setValidationStatus((s) => ({ ...s, aws: "valid" }));
  };


  const validateHashicorpVault = async (address, token) => {
    setValidationStatus((s) => ({ ...s, hashicorp: "valid" }));
  };

  const validateGeminiApiKey = async (apiKey, model) => {
    setValidationStatus((s) => ({ ...s, gemini: "valid" }));
  };

  const validateSlackCredentials = async (botToken, botUserId, userToken) => {
    setValidationStatus((s) => ({ ...s, slack: "valid" }));
  };

  const validateJiraCredentials = async (
    url,
    username,
    apiToken,
    projectKey
  ) => {
    setValidationStatus((s) => ({ ...s, jira: "valid" }));
  };

  const validateOpenAiApiKey = async (apiKey, baseUrl) => {
    setValidationStatus((s) => ({ ...s, openai: "valid" }));
  };

  const validateTeamsCredentials = async (
    tenantId,
    appClientId,
    appClientSecret
  ) => {
    setValidationStatus((s) => ({ ...s, teams: "valid" }));
  };

  const validateGitlabPat = async (tokenToValidate) => {
    setValidationStatus((s) => ({ ...s, gitlab: "valid" }));
  };

  const validateBitbucketPat = async (tokenToValidate) => {
    setValidationStatus((s) => ({ ...s, bitbucket: "valid" }));
  };

  const validateGiteaPat = async (tokenToValidate) => {
    setValidationStatus((s) => ({ ...s, gitea: "valid" }));
  };

  const validateAzureCredentials = async (
    tenantId,
    clientId,
    clientSecret,
    subscriptionId
  ) => {
    setValidationStatus((s) => ({ ...s, azure: "valid" }));
  };

  const validateGcpCredentials = async (config) => {
    setValidationStatus((s) => ({ ...s, gcp: "valid" }));
  };

  const handleTeamsLogin = async () => {
    // Show toast for USP feature
    showFreeAssessmentToast();
    setTeamsOAuthLoading(false);
  };

  // Trigger initial validations once config is loaded
  useEffect(() => {
    if (!loading && !hasFailed) {
      validateGithubPat(config.github_pat);
      validateGitlabPat(config.gitlab_pat);
      validateBitbucketPat(config.bitbucket_pat);
      validateGiteaPat(config.gitea_pat);
      validateAwsCredentials(
        config.aws_access_key_id,
        config.aws_secret_access_key,
        config.aws_region
      );
      validateAzureCredentials(
        config.azure_tenant_id,
        config.azure_client_id,
        config.azure_client_secret,
        config.azure_subscription_id // This field doesn't exist in config, will be undefined
      );
      validateGcpCredentials(config);
      validateHashicorpVault(
        config.hashicorp_vault_url,
        config.hashicorp_vault_token
      );
      validateGeminiApiKey(config.gemini_api_key, config.llm_model_id);
      validateSlackCredentials(
        config.slack_bot_token,
        config.slack_bot_user_id,
        config.slack_user_token
      );
      validateJiraCredentials(
        config.jira_url,
        config.jira_username,
        config.jira_api_token,
        config.jira_project_key
      );
      validateOpenAiApiKey(config.openai_api_key, config.openai_base_url);
      validateTeamsCredentials(
        config.teams_tenant_id,
        config.teams_app_client_id,
        config.teams_app_client_secret
      );
    }
    // eslint-disable-next-line
  }, [loading, hasFailed]);

  // Open create account dialog by default if no storage accounts exist
  useEffect(() => {
    if (selectedCategory === "cloudStorage" && !loadingStorageAccounts) {
      if (!storageAccounts || storageAccounts.length === 0) {
        setShowCreateAccountDialog(true);
      } else {
        // Close dialog if accounts exist
        setShowCreateAccountDialog(false);
      }
    }
  }, [selectedCategory, loadingStorageAccounts, storageAccounts]);

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const isSensitiveField = (field) =>
    [
      "github_pat",
      "gitlab_pat",
      "bitbucket_pat",
      "gitea_pat",
      "jira_api_token",
      "aws_access_key_id",
      "aws_secret_access_key",
      "gcp_private_key",
      "gcp_private_key_id",
      "slack_bot_token",
      "slack_user_token",
      "slack_signing_secret",
      "freshservice_api_key",
      "app_client_secret",
      "webhook_secret",
      "github_webhook_secret",
      "teams_app_client_secret",
      "teams_webhook_secret",
      "azure_client_secret",
    ].includes(field);

  // Helper function to truncate long JSON strings for preview
  const truncateJsonPreview = (jsonString, maxLength = 100) => {
    if (!jsonString || typeof jsonString !== 'string') return jsonString;
    if (jsonString.length <= maxLength) return jsonString;

    // Try to format as JSON first to get a cleaner truncation
    try {
      const parsed = JSON.parse(jsonString);
      const formatted = JSON.stringify(parsed, null, 2);
      if (formatted.length <= maxLength) return formatted;

      // Truncate formatted JSON
      const truncated = formatted.substring(0, maxLength);
      // Try to end at a complete line
      const lastNewline = truncated.lastIndexOf('\n');
      if (lastNewline > maxLength * 0.7) {
        return truncated.substring(0, lastNewline) + '\n...';
      }
      return truncated + '...';
    } catch {
      // If not valid JSON, just truncate the string
      return jsonString.substring(0, maxLength) + '...';
    }
  };

  const getDisplayValue = (field, value, isAiService = false) => {
    // Return sample values for empty fields
    if (!value || value === "") {
      const sampleValues = {
        github_pat: "ghp_xxxxxxxxxxxxxxxxxxxx",
        gitlab_pat: "glpat-xxxxxxxxxxxxxxxxxxxx",
        bitbucket_pat: "ATBBxxxxxxxxxxxxxxxx",
        bitbucket_workspace: "your-workspace-name",
        jira_url: "https://yourcompany.atlassian.net",
        jira_username: "john.doe@company.com",
        jira_api_token: "ATATT3xFfGF0xxxxxxxxxxxxxxxxxxxx",
        slack_webhook_url:
          "https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX",
        slack_channel: "#security-alerts",
        aws_access_key_id: "AKIAIOSFODNN7EXAMPLE",
        aws_secret_access_key: "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY",
        aws_region: "us-east-1",
        azure_tenant_id: "12345678-1234-1234-1234-123456789012",
        azure_client_id: "87654321-4321-4321-4321-210987654321",
        azure_client_secret: "your-client-secret-here",
        azure_storage_account: "mystorageaccount",
        gcp_project_id: "your-gcp-project-id",
        gcp_private_key_id: "your-gcp-private-key-id",
        gcp_private_key: "-----BEGIN PRIVATE KEY-----\n...",
        gcp_client_email: "your-service-account",
        gcp_client_id: "your-gcp-client-id",
        gcp_auth_uri: "https://accounts.google.com/o/oauth2/auth",
        gcp_token_uri: "https://oauth2.googleapis.com/token",
        gcp_auth_provider_x509_cert_url:
          "https://www.googleapis.com/oauth2/v1/certs",
        gcp_client_x509_cert_url: "https://www.googleapis.com/robot/v1",
        gcp_universe_domain: "googleapis.com",
        teams_tenant_id: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        teams_app_client_id: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        teams_app_client_secret: "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        teams_webhook_url: "https://outlook.office.com/webhook/xxxxxxxx",
        teams_webhook_secret: "your-teams-webhook-secret-here",
        teams_security_team_id: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        teams_security_channel_id: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        teams_redirect_uri: "https://your-app.com/auth/teams",
        teams_public_url: "https://your-app.com",
        teams_subscription_expiry_days: "30",
        freshservice_api_key: "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        freshservice_domain: "yourcompany.freshservice.com",
        subscription_expiry_days: "30",
        app_client_id: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        app_client_secret: "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        webhook_secret: "your-webhook-secret-key-here",
        auto_ticket_creation: "true",
        github_webhook_secret: "your-github-webhook-secret-here",
        // AI Service sample values
        gemini_api_key: "AIzaSyxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        llm_model_id: "gemini-pro",
        openai_model_id: "gpt-4",
        openai_api_key: "sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        openai_base_url: "https://api.openai.com/v1",
        anthropic_api_key: "sk-ant-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        claude_model_id: "claude-3-sonnet-20240229",
        groq_api_key: "gsk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        groq_model_id: "llama3-8b-8192",
        deepseek_api_key: "sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        deepseek_model_id: "deepseek-chat",
        deepseek_base_url: "https://api.deepseek.com/v1",
        llama_api_key: "llama-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        llama_model_id: "llama-3-8b",
        qwen_auth_token: "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        qwen_cookie: "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        qwen_model_id: "qwen-turbo",
        hashicorp_vault_url: "https://vault.company.com:8200",
        hashicorp_vault_token: "hvs.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        hashicorp_vault_mount_path: "secret",
      };
      return sampleValues[field] || "enter-credentials";
    }

    const isSensitive = isAiService
      ? isAiSensitiveField(field)
      : isSensitiveField(field);
    if (!isSensitive) {
      // For JSON fields, truncate for preview
      if (field === 'gcp_service_account_json' && value && typeof value === 'string') {
        return truncateJsonPreview(value, 150);
      }
      return value;
    }
    if (showPasswords[field]) return value;
    return "********";
  };

  const getFieldLabel = (field) => {
    const labels = {
      github_pat: "GitHub Personal Access Token",
      gitlab_pat: "GitLab Personal Access Token",
      bitbucket_pat: "Bitbucket App Password",
      gitea_pat: "Gitea Personal Access Token",
      gitea_instance_url: "Gitea Instance URL",
      bitbucket_workspace: "Bitbucket Workspace",
      jira_url: "Jira URL",
      jira_username: "Jira Username",
      jira_api_token: "Jira API Token",
      jira_project_key: "Jira Project Key",
      aws_access_key_id: "AWS access key ID",
      aws_secret_access_key: "AWS secret access key ID",
      aws_region: "AWS region",
      azure_tenant_id: "Azure Tenant ID",
      azure_client_id: "Azure Client ID",
      azure_client_secret: "Azure Client Secret",
      azure_storage_account: "Azure Storage Account",
      gcp_project_id: "GCP Project ID",
      gcp_private_key_id: "GCP Private Key ID",
      gcp_private_key: "GCP Private Key",
      gcp_client_email: "GCP Client Email",
      gcp_client_id: "GCP Client ID",
      gcp_auth_uri: "GCP Auth URI",
      gcp_token_uri: "GCP Token URI",
      gcp_auth_provider_x509_cert_url: "GCP Auth Provider x509 Cert URL",
      gcp_client_x509_cert_url: "GCP Client x509 Cert URL",
      gcp_universe_domain: "GCP Universe Domain",
      slack_bot_token: "Slack Bot Token",
      slack_bot_user_id: "Slack Bot User ID",
      slack_user_token: "Slack User Token",
      slack_alert_channel: "Slack Alert Channel",
      slack_signing_secret: "Slack Signing Secret",
      slack_blacklisted_channels: "Slack Blacklisted Channels",
      slack_events_enabled: "Slack Events Enabled",
      slack_history_enabled: "Slack History Enabled",
      freshservice_email_id: "Freshservice Email ID",
      freshservice_url: "Freshservice URL",
      freshservice_api_key: "Freshservice API Key",
      teams_tenant_id: "Teams Tenant ID",
      teams_app_client_id: "Teams App Client ID",
      teams_app_client_secret: "Teams App Client Secret",
      teams_webhook_url: "Teams Webhook URL",
      teams_webhook_secret: "Teams Webhook Secret",
      teams_security_team_id: "Teams Security Team ID",
      teams_security_channel_id: "Teams Security Channel ID",
      teams_redirect_uri: "Teams Redirect URI",
      teams_public_url: "Teams Public URL",
      teams_subscription_expiry_days: "Teams Subscription Expiry Days",
      auto_ticket_creation: "Auto Ticket Creation",
      github_webhook_secret: "GitHub Webhook Secret",
    };
    return labels[field] || field;
  };

  const awsRegions = [
    { label: "US East (N. Virginia)", value: "us-east-1" },
    { label: "US West (N. California)", value: "us-west-1" },
    { label: "US West (Oregon)", value: "us-west-2" },
    { label: "EU (Ireland)", value: "eu-west-1" },
    { label: "Asia Pacific (Singapore)", value: "ap-southeast-1" },
    { label: "Asia Pacific (Tokyo)", value: "ap-northeast-1" },
    { label: "Asia Pacific (Seoul)", value: "ap-northeast-2" },
    { label: "Asia Pacific (Mumbai)", value: "ap-south-1" },
    { label: "EU (Stockholm)", value: "eu-north-1" },
    { label: "EU (Frankfurt)", value: "eu-central-1" },
    { label: "EU (London)", value: "eu-west-2" },
    { label: "EU (Paris)", value: "eu-west-3" },
    { label: "South America (Sao Paulo)", value: "sa-east-1" },
  ];

  const getAiFieldLabel = (field) => {
    const labels = {
      gemini_api_key: "Gemini API Key",
      llm_model_id: "Model ID",
      openai_model_id: "OpenAI Model ID",
      openai_api_key: "OpenAI API Key",
      openai_base_url: "OpenAI Base URL",
      anthropic_api_key: "Anthropic API Key",
      claude_model_id: "Claude Model ID",
      groq_api_key: "Groq API Key",
      groq_model_id: "Groq Model ID",
      deepseek_api_key: "DeepSeek API Key",
      deepseek_model_id: "DeepSeek Model ID",
      deepseek_base_url: "DeepSeek Base URL",
      llama_api_key: "Llama API Key",
      llama_model_id: "Llama Model ID",
      qwen_auth_token: "Qwen Auth Token",
      qwen_cookie: "Qwen Cookie",
      qwen_model_id: "Qwen Model ID",
      hashicorp_vault_url: "HashiCorp Vault URL",
      hashicorp_vault_token: "HashiCorp Vault Token",
      hashicorp_vault_mount_path: "HashiCorp Vault Mount Path",
    };
    return labels[field] || field;
  };

  // Check if AI field is sensitive
  const isAiSensitiveField = (field) => {
    const sensitiveFields = [
      "gemini_api_key",
      "openai_api_key",
      "anthropic_api_key",
      "groq_api_key",
      "deepseek_api_key",
      "llama_api_key",
      "qwen_auth_token",
      "qwen_cookie",
      "hashicorp_vault_token",
    ];
    return sensitiveFields.includes(field);
  };

  if (loading && !initialDataLoaded) {
    return (
      <div className="space-y-6">

        <div className="space-y-6">
          {[...Array(4)].map((_, i) => (
            <ShimmerCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (hasFailed) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-red-600 mb-4">
            <svg
              className="w-12 h-12 mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <p className="text-center font-medium">
              {errorMsg || "Failed to load configuration"}
            </p>
          </div>
          <button
            onClick={() => {
              setHasFailed(false);
              setLoading(true);
            }}
            className="px-4 py-2 bg-gradient-to-r from-[#6D49FE] to-[#5a3de8] text-white rounded-lg hover:from-[#5a3de8] hover:to-[#6D49FE] transition-all duration-200 font-medium shadow-sm hover:shadow-md"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const getFilteredSections = () => {
    // If no category is selected, return empty array
    if (!selectedCategory) {
      return [];
    }

    const baseSections = [];

    // Special case for file extensions only
    if (selectedCategory === "fileExtensions") {
      return [
        {
          title: "File Extensions",
          icon: <FileText className="w-6 h-6" />,
          fields: ["extensions"],
          isFileExtensions: true,
        },
      ];
    }

    // Filter sections based on selected category
    switch (selectedCategory) {
      case "cloudStorage":
        baseSections.push(
          {
            title: "Amazon Web Service",
            icon: "/settingspage/aws.svg.png",
            fields: [
              "aws_access_key_id",
              "aws_secret_access_key",
              "aws_region",
            ],
          },
          {
            title: "Azure",
            icon: "/inte/Azure.png",
            fields: [
              "azure_tenant_id",
              "azure_client_id",
              "azure_client_secret",
              "azure_storage_account",
            ],
          },
          {
            title: "Google Cloud Platform",
            icon: "/inte/GCP.png",
            fields: [
              "gcp_project_id",
              "gcp_private_key_id",
              "gcp_private_key",
              "gcp_client_email",
              "gcp_client_id",
              "gcp_auth_uri",
              "gcp_token_uri",
              "gcp_auth_provider_x509_cert_url",
              "gcp_client_x509_cert_url",
              "gcp_universe_domain",
            ],
          }
        );
        break;

      case "vcs":
        // Show all VCS options like cloud storage
        baseSections.push(
          {
            title: "GitHub",
            icon: "/settingspage/github.svg.png",
            fields: ["github_pat", "github_webhook_secret"],
          },
          {
            title: "GitLab",
            icon: "/gitlab.svg",
            fields: ["gitlab_pat"],
          },

          {
            title: "Bitbucket",
            icon: "/bitbucket.png",
            fields: ["bitbucket_pat", "bitbucket_workspace"],
          },
          {
            title: "Gitea",
            icon: "/inte/gitea.png",
            fields: ["gitea_pat"],
          }
        );
        break;

      case "communication":
        // Show only the selected communication service
        if (preferredCommunicationService === "slack") {
          baseSections.push({
            title: "Slack",
            icon: "/settingspage/slack.png",
            fields: [
              "slack_bot_token",
              "slack_bot_user_id",
              "slack_user_token",
              "slack_alert_channel",
              "slack_signing_secret",
              "slack_blacklisted_channels",
              "slack_events_enabled",
              "slack_history_enabled",
            ],
          });
        } else if (preferredCommunicationService === "msteams") {
          baseSections.push({
            title: "Microsoft Teams",
            icon: "/settingspage/msteams.webp",
            fields: [
              "teams_tenant_id",
              "teams_app_client_id",
              "teams_app_client_secret",
              "teams_webhook_url",
              "teams_webhook_secret",
              "teams_security_team_id",
              "teams_security_channel_id",
              "teams_redirect_uri",
              "teams_public_url",
              "teams_subscription_expiry_days",
            ],
          });
        }
        break;

      case "vault":
        // Add HashiCorp Vault section only if vault provider is hashicorp
        if (vaultProvider === "hashicorp") {
          baseSections.push({
            title: "HashiCorp Vault",
            icon: "/ai-logos/hashicorp.svg",
            fields: categories.vault.services.hashicorp?.fields || [],
            isAiService: true,
          });
        }
        break;

      case "aiModel":
        // Add AI Service section based on preferred LLM provider
        const providerConfig = categories.aiModel.services[preferredLlmProvider];
        const aiProviderTitle = providerConfig
          ? providerConfig.name
          : preferredLlmProvider.charAt(0).toUpperCase() +
            preferredLlmProvider.slice(1);

        // Map provider names to their corresponding logo files
        const aiProviderIcons = {
          gemini: "/ai-logos/gemini.png",
          openai: "/ai-logos/openai.webp",
          anthropic: "/ai-logos/anthropic.webp",
          groq: "/ai-logos/groq.png",
          deepseek: "/ai-logos/deepseek.png",
          llama: "/ai-logos/llama.jpg",
          qwen: "/ai-logos/qwen.png",
        };

        const aiProviderIcon =
          aiProviderIcons[preferredLlmProvider] ||
          `/settingspage/${preferredLlmProvider}.png`;

        baseSections.push({
          title: `${aiProviderTitle} AI Service`,
          icon: aiProviderIcon,
          fields:
            categories.aiModel.services[preferredLlmProvider]?.fields || [],
          isAiService: true,
        });
        break;

      case "ticketing":
        if (preferredTicketingService === "jira") {
          baseSections.push({
            title: "Jira",
            icon: "/settingspage/jira.png",
            fields: [
              "jira_url",
              "jira_username",
              "jira_api_token",
              "jira_project_key",
              "auto_ticket_creation",
            ],
          });
        } else {
          baseSections.push({
            title: "Freshservice",
            icon: "/settingspage/freshworks.jpg",
            fields: [
              "freshservice_email_id",
              "freshservice_url",
              "freshservice_api_key",
              "auto_ticket_creation",
            ],
          });
        }
        break;

      default:
        break;
    }

    return baseSections;
  };

  // Returns documentation URL based on section title
  const getDocUrlForSection = (title) => {
    switch ((title || "").toLowerCase()) {
      case "amazon web service":
      case "aws":
        return "https://www.stackguard.io/docs/guide/aws/";
      case "jira":
        return "https://www.stackguard.io/docs/guide/jira/";
      case "freshservice":
      case "freshworks":
        return "https://www.stackguard.io/docs/guide/freshworks/";
      case "slack":
        return "https://www.stackguard.io/docs/guide/slack/";
      case "microsoft teams":
        return "https://www.stackguard.io/docs/guide/microsoft-teams/";
      case "github":
        return "https://www.stackguard.io/docs/guide/github/";
      case "gitlab":
        return "https://www.stackguard.io/docs/guide/gitlab/";
      case "bitbucket":
        return "https://www.stackguard.io/docs/guide/bitbucket/";
      case "google cloud platform":
        return "https://www.stackguard.io/docs/guide/gcp/";
      case "google ai service":
      case "gemini ai service":
        return "https://www.stackguard.io/docs/guide/llm/google/";
      case "openai ai service":
        return "https://www.stackguard.io/docs/guide/llm/openai/";
      case "anthropic ai service":
        return "https://www.stackguard.io/docs/guide/llm/anthropic/";
      case "groq ai service":
        return "https://www.stackguard.io/docs/guide/llm/groq/";
      case "deepseek ai service":
        return "https://www.stackguard.io/docs/guide/llm/deepseek/";
      case "llama ai service":
        return "https://www.stackguard.io/docs/guide/llm/llama/";
      case "qwen ai service":
        return "https://www.stackguard.io/docs/guide/llm/qwen/";
      case "hashicorp vault":
        return "https://www.stackguard.io/docs/guide/vault/hashicorp/";
      case "file extensions":
        return "https://www.stackguard.io/docs/guide/file-extensions/";
      default:
        return "https://www.stackguard.io/docs/setup/prereq";
    }
  };

  const ServiceCard = ({
    section,
    fields,
    isEditingSection,
    enterEditMode,
    exitEditMode,
    forceExpanded = false,
    hideHeaderActions = false,
  }) => {
    const [sectionConfig, setSectionConfig] = useState({});
    const isExpanded = forceExpanded || expandedCard === section.title;

    // Initialize section config when component mounts or fields change
    useEffect(() => {
      const initialConfig = {};
      fields.forEach((field) => {
        // Handle boolean fields properly to avoid converting false to empty string
        if (
          field === "auto_ticket_creation" ||
          field === "slack_events_enabled" ||
          field === "slack_history_enabled"
        ) {
          initialConfig[field] =
            config[field] !== undefined ? config[field] : false;
        } else if (field === "extensions" && section.isFileExtensions) {
          // Handle file extensions array
          initialConfig[field] = fileExtensions || [];
        } else {
          initialConfig[field] = config[field] || "";
        }
      });
      setSectionConfig(initialConfig);
    }, [fields, section.isFileExtensions, config]);

    const handleSectionEdit = () => {
      // Always expand and go straight into edit mode in one click
      setExpandedCard(section.title);
      const initialConfig = {};
      fields.forEach((field) => {
        // Handle boolean fields properly to avoid converting false to empty string
        if (
          field === "auto_ticket_creation" ||
          field === "slack_events_enabled" ||
          field === "slack_history_enabled"
        ) {
          initialConfig[field] =
            config[field] !== undefined ? config[field] : false;
        } else if (field === "extensions" && section.isFileExtensions) {
          // Handle file extensions array
          initialConfig[field] = fileExtensions || [];
        } else {
          initialConfig[field] = config[field] || "";
        }
      });
      setSectionConfig(initialConfig);
      // Reveal all sensitive fields in this section while editing
      setShowPasswords((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          const isSensitive = section.isAiService
            ? isAiSensitiveField(field)
            : isSensitiveField(field);
          if (isSensitive) {
            next[field] = true;
          }
        });
        return next;
      });
      enterEditMode(section.title);
    };

    const handleSectionSave = async () => {
      try {
        setSaving(true);
        // Static: No auth needed

        // Handle file extensions separately - use mock data
        if (section.isFileExtensions) {
          const extensions = sectionConfig.extensions || [];
          // Normalize extensions (remove duplicates, ensure they start with .)
          const normalizedExtensions = [...new Set(extensions.map(ext => 
            ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`
          ))];

          setFileExtensions(normalizedExtensions);
          exitEditMode();
          showFreeAssessmentToast();
          return;
        }

        // Update all fields in the section
        const updatedConfig = { ...config };
        fields.forEach((field) => {
          updatedConfig[field] = sectionConfig[field];
        });

        // Build payload - start with preferences and only include fields from current section
        // This reduces payload size significantly by not sending all config fields
        const payload = {
          preferred_tool: preferredTicketingService,
          preferred_provider: preferredGitProvider,
          llm_provider_pref: preferredLlmProvider,
          vault_pref: vaultProvider,
        };

        // Only include fields from the current section being edited
        fields.forEach((field) => {
          const value = updatedConfig[field];

          // Skip the large gcp_service_account_json field (it's display-only, GCP uses structured object)
          if (field === "gcp_service_account_json") {
            return;
          }

          // Include field if it has a meaningful value
          // Include: non-empty strings, booleans (including false), numbers (including 0), non-empty arrays
          // Exclude: empty strings, null, undefined, and very large strings (>50KB) that might cause payload issues
          const shouldInclude =
            value !== null &&
            value !== undefined &&
            (typeof value === "boolean" ||
              typeof value === "number" ||
              (Array.isArray(value) && value.length > 0) ||
              (typeof value === "string" && value.length > 0 && value.length <= 50000));

          if (shouldInclude) {
            payload[field] = value;
          }
        });

        // copy bitbucket_app_password into bitbucket_pat if provided
        ensureBitbucketPat(payload);

        if (section.title === "Google Cloud Platform") {
          const gcpServiceAccount = {
            type: "service_account",
            project_id: updatedConfig.gcp_project_id,
            private_key_id: updatedConfig.gcp_private_key_id,
            private_key: updatedConfig.gcp_private_key,
            client_email: updatedConfig.gcp_client_email,
            client_id: updatedConfig.gcp_client_id,
            auth_uri: updatedConfig.gcp_auth_uri,
            token_uri: updatedConfig.gcp_token_uri,
            auth_provider_x509_cert_url:
              updatedConfig.gcp_auth_provider_x509_cert_url,
            client_x509_cert_url: updatedConfig.gcp_client_x509_cert_url,
            universe_domain: updatedConfig.gcp_universe_domain,
          };

          if (Object.values(gcpServiceAccount).some((v) => v)) {
            payload.gcp_service_account = gcpServiceAccount;
          } else {
            delete payload.gcp_service_account;
          }

          delete payload.gcp_project_id;
          delete payload.gcp_private_key_id;
          delete payload.gcp_private_key;
          delete payload.gcp_client_email;
          delete payload.gcp_client_id;
          delete payload.gcp_auth_uri;
          delete payload.gcp_token_uri;
          delete payload.gcp_auth_provider_x509_cert_url;
          delete payload.gcp_client_x509_cert_url;
          delete payload.gcp_universe_domain;
        }

        // If we're editing an existing storage account via the legacy section,
        // update the storage account endpoint instead of the general config
        if (editingAccountId) {
          try {
            // AWS
            if (section.title === "Amazon Web Service") {
              const accountPayload = {
                name: payload.name || config.aws_account_name || "aws-account",
                provider: "s3",
                aws: {
                  access_key_id:
                    payload.aws_access_key_id || config.aws_access_key_id,
                  secret_access_key:
                    payload.aws_secret_access_key ||
                    config.aws_secret_access_key,
                  region: payload.aws_region || config.aws_region,
                },
              };

              // Static: Update account in local state
              setStorageAccounts(storageAccounts.map(acc => 
                (acc.id || acc.account_id) === editingAccountId
                  ? { ...acc, ...accountPayload }
                  : acc
              ));
              fetchStorageAccounts();
              exitEditMode();
              setEditingAccountId(null);
              setShowLegacyEditor(false);
              showFreeAssessmentToast();
              setSaving(false);
              return;
            }

            // Azure
            if (section.title === "Azure") {
              const accountPayload = {
                name:
                  payload.name || config.azure_account_name || "azure-account",
                provider: "azure",
                azure: {
                  tenant_id: payload.azure_tenant_id || config.azure_tenant_id,
                  client_id: payload.azure_client_id || config.azure_client_id,
                  client_secret:
                    payload.azure_client_secret || config.azure_client_secret,
                  storage_account:
                    payload.azure_storage_account ||
                    config.azure_storage_account,
                },
              };

              // Static: Update account in local state
              setStorageAccounts(storageAccounts.map(acc => 
                (acc.id || acc.account_id) === editingAccountId
                  ? { ...acc, ...accountPayload }
                  : acc
              ));
              fetchStorageAccounts();
              exitEditMode();
              setEditingAccountId(null);
              setShowLegacyEditor(false);
              showFreeAssessmentToast();
              setSaving(false);
              return;
            }

            // GCP
            if (section.title === "Google Cloud Platform") {
              const gcpServiceAccount = {
                type: "service_account",
                project_id: payload.gcp_project_id || config.gcp_project_id,
                private_key_id:
                  payload.gcp_private_key_id || config.gcp_private_key_id,
                private_key: payload.gcp_private_key || config.gcp_private_key,
                client_email:
                  payload.gcp_client_email || config.gcp_client_email,
                client_id: payload.gcp_client_id || config.gcp_client_id,
                auth_uri: payload.gcp_auth_uri || config.gcp_auth_uri,
                token_uri: payload.gcp_token_uri || config.gcp_token_uri,
                auth_provider_x509_cert_url:
                  payload.gcp_auth_provider_x509_cert_url ||
                  config.gcp_auth_provider_x509_cert_url,
                client_x509_cert_url:
                  payload.gcp_client_x509_cert_url ||
                  config.gcp_client_x509_cert_url,
                universe_domain:
                  payload.gcp_universe_domain || config.gcp_universe_domain,
              };

              // Build payload using backend-expected `gcp` shape.
              // New API expects the full service_account JSON (including project_id)
              const serviceAccountForPayload = { ...gcpServiceAccount };
              // If a project id was provided separately, ensure it's present inside the service account
              const projId =
                serviceAccountForPayload.project_id ||
                payload.gcp_project_id ||
                config.gcp_project_id;
              if (projId) {
                serviceAccountForPayload.project_id = projId;
              }

              const accountPayload = {
                name: payload.name || config.gcp_account_name || "gcp-account",
                provider: "gcp",
                gcp: {
                  // Only include the service_account object; project_id should be part of it
                  ...(Object.keys(serviceAccountForPayload).length
                    ? { service_account: serviceAccountForPayload }
                    : {}),
                },
              };

              // Static: Update account in local state
              setStorageAccounts(storageAccounts.map(acc => 
                (acc.id || acc.account_id) === editingAccountId
                  ? { ...acc, ...accountPayload }
                  : acc
              ));
              fetchStorageAccounts();
              exitEditMode();
              setEditingAccountId(null);
              setShowLegacyEditor(false);
              showFreeAssessmentToast();
              setSaving(false);
              return;
            }
          } catch (err) {
            console.error("Error updating storage account:", err);
            const message =
              err.response?.data?.message || "Failed to update storage account";
            showToast(message, "error");
            setSaving(false);
            return;
          }
        }

      // For USP features, show toast instead of saving
      showFreeAssessmentToast();
      exitEditMode();
      setSaving(false);
      } catch (err) {
        console.error("Error saving section:", err);
        const message =
          err.response?.data?.message || "Failed to update configuration";
        showToast(message, "error");
      } finally {
        setSaving(false);
      }
    };

    const handleSectionCancel = () => {
      exitEditMode();
      // Reset section config to original values
      const originalConfig = {};
      fields.forEach((field) => {
        // Handle boolean fields properly to avoid converting false to empty string
        if (field === "auto_ticket_creation") {
          originalConfig[field] =
            config[field] !== undefined ? config[field] : false;
        } else if (field === "extensions" && section.isFileExtensions) {
          // Handle file extensions array
          originalConfig[field] = fileExtensions || [];
        } else {
          originalConfig[field] = config[field] || "";
        }
      });
      setSectionConfig(originalConfig);
      // Hide sensitive fields when cancelling edit
      setShowPasswords((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          const isSensitive = section.isAiService
            ? isAiSensitiveField(field)
            : isSensitiveField(field);
          if (isSensitive) {
            next[field] = false;
          }
        });
        return next;
      });
    };

    return (
      <Card className="border shadow-sm">
        <CardHeader
          className="flex flex-row items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors duration-200 w-full"
          onClick={(e) => {
            // Ignore clicks from elements that should not toggle the accordion
            if (
              e.target &&
              typeof e.target.closest === "function" &&
              e.target.closest('[data-no-toggle="true"]')
            ) {
              return;
            }
            toggleCardExpansion(section.title);
          }}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {typeof section.icon === "string" ? (
              <img
                src={section.icon}
                alt={section.title}
                className="w-6 h-6 object-contain bg-white rounded-sm p-0.5"
              />
            ) : (
              <div className="w-6 h-6 flex items-center justify-center text-gray-600">
                {section.icon}
              </div>
            )}
            <CardTitle className="text-base flex items-center gap-2">
              {section.title}
              {/* Larger info icon next to the title */}
              <a
                href={getDocUrlForSection(section.title)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center text-gray-500 hover:text-gray-700"
                aria-label={`${section.title} documentation`}
              >
                <FiInfo className="h-5 w-5 md:h-6 md:w-6" />
              </a>
              {/* Validation badge */}
              {(() => {
                let status = "unknown";
                if (section.title === "GitHub")
                  status = validationStatus.github;
                else if (section.title === "GitLab")
                  status = validationStatus.gitlab;
                else if (section.title === "Bitbucket")
                  status = validationStatus.bitbucket;
                else if (section.title === "Gitea")
                  status = validationStatus.gitea;
                else if (section.title === "Amazon Web Service")
                  status = validationStatus.aws;
                else if (section.title === "Azure")
                  status = validationStatus.azure;
                else if (section.title === "Google Cloud Platform")
                  status = validationStatus.gcp;
                else if (section.title === "HashiCorp Vault")
                  status = validationStatus.hashicorp;
                else if (
                  section.title === "Google AI Service" ||
                  section.title === "Gemini AI Service"
                )
                  status = validationStatus.gemini;
                else if (section.title === "Slack")
                  status = validationStatus.slack;
                else if (section.title === "Microsoft Teams")
                  status = validationStatus.teams;
                else if (section.title === "Jira")
                  status = validationStatus.jira;
                else if (section.title === "Freshservice")
                  status = validationStatus.freshservice;
                else if (section.title === "OpenAI AI Service")
                  status = validationStatus.openai;
                else if (section.title === "Anthropic AI Service")
                  status = validationStatus.anthropic;
                else if (section.title === "Groq AI Service")
                  status = validationStatus.groq;
                else if (section.title === "DeepSeek AI Service")
                  status = validationStatus.deepseek;
                else if (section.title === "Llama AI Service")
                  status = validationStatus.llama;
                else if (section.title === "Qwen AI Service")
                  status = validationStatus.qwen;

                if (status === "valid") {
                  return (
                    <span className="ml-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Verified
                      </span>
                    </span>
                  );
                }
                return null;
              })()}
              {/* Show vault badge for AWS section when vault provider is AWS Parameter Store */}
              {section.title === "Amazon Web Service" &&
                vaultProvider === "aws_parameter_store" && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Vault
                          </span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Same credentials being used for vault as well</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
            </CardTitle>
          </div>
          {!hideHeaderActions && (
            <div className="flex items-center gap-1 shrink-0">
              {/* Teams Login Button - show when credentials configured but OAuth needed */}
              {section.title === "Microsoft Teams" && !isEditingSection && (
                <Button
                  size="sm"
                  data-no-toggle="true"
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTeamsLogin();
                  }}
                  disabled={teamsOAuthLoading}
                  className="border border-blue-600 bg-blue-600/10 text-blue-600 hover:bg-blue-600/20 mr-2"
                >
                  {teamsOAuthLoading ? "Logging in..." : "Login with Teams"}
                </Button>
              )}
              {!isEditingSection && (
                <Button
                  size="sm"
                  data-no-toggle="true"
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSectionEdit();
                  }}
                  className="border border-[#44087D] bg-[#44087D]/10 text-[#44087D] hover:bg-[#44087D]/20"
                >
                  Edit
                </Button>
              )}
              {/* Static chevron without animation */}
              <div className="ml-1">
                <FiChevronDown
                  className={`h-4 w-4 ${isExpanded ? "rotate-180" : ""}`}
                />
              </div>
            </div>
          )}
        </CardHeader>

        {isExpanded && (
          <div className="overflow-hidden w-full">
            <CardContent className="p-0 w-full overflow-x-hidden">
              {/* Table Header Strip
                  <div className="grid grid-cols-2 bg-gray-100 px-4 py-2 text-sm font-medium">
                    <div>Field name</div>
                    <div className="text-right">Value</div>
                  </div>
                    */}
              {/* Content */}
              {section.isFileExtensions ? (
                // Special full-width layout for file extensions
                <div className="p-4">
                  {isEditingSection ? (
                    <FileExtensionInput
                      value={sectionConfig.extensions || []}
                      onChange={(newExtensions) =>
                        setSectionConfig((prev) => ({
                          ...prev,
                          extensions: newExtensions,
                        }))
                      }
                      className="w-full"
                    />
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-700">
                          Blacklisted File Extensions ({fileExtensions.length})
                        </div>
                        {fileExtensions.length > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                fileExtensions.join(", ")
                              );
                              showToast(
                                "Extensions copied to clipboard",
                                "info"
                              );
                            }}
                            className="h-6 text-xs"
                          >
                            <FiCopy className="h-3 w-3 mr-1" />
                            Copy All
                          </Button>
                        )}
                      </div>
                      <div className="min-h-[80px] bg-gray-50 border rounded-lg p-4">
                        {fileExtensions && fileExtensions.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {fileExtensions.map((ext, index) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className="font-medium text-sm bg-white px-3 py-1"
                              >
                                {ext}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-16">
                            <span className="text-gray-400 italic text-sm">
                              No file extensions configured
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        These file extensions will be included in security scans
                        across your organization.
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Standard field layout for other sections
                <div className="divide-y">
                  {fields.map((field) => {
                    // Ensure value is never undefined to avoid uncontrolled -> controlled warnings
                    const value = isEditingSection
                      ? sectionConfig[field] ?? ""
                      : config[field] ?? "";
                    const isSensitive = section.isAiService
                      ? isAiSensitiveField(field)
                      : isSensitiveField(field);
                    const fieldLabel = section.isAiService
                      ? getAiFieldLabel(field)
                      : getFieldLabel(field);

                    return (
                      <div
                        key={field}
                        className="grid grid-cols-2 items-center px-4 py-3 text-sm"
                      >
                        {/* Field Name */}
                        <div className="flex items-center gap-2">
                          <Label className="font-medium text-sm">
                            {fieldLabel}
                          </Label>
                          <Badge
                            variant="primary"
                            className="text-xs bg-white text-gray-700"
                          >
                            Required
                          </Badge>
                        </div>

                        {/* Field Value */}
                        <div className="flex items-center justify-end w-full">
                          {isEditingSection ? (
                            <div className="flex items-center w-full justify-end">
                              {field === "auto_ticket_creation" ||
                              field === "slack_events_enabled" ||
                              field === "slack_history_enabled" ? (
                                <div className="flex items-center">
                                  <Select
                                    value={value ? "true" : "false"}
                                    onValueChange={(newValue) => {
                                      setSectionConfig((prev) => ({
                                        ...prev,
                                        [field]: newValue === "true",
                                      }));
                                    }}
                                  >
                                    <SelectTrigger className="w-96 h-10">
                                      <SelectValue
                                        placeholder={`Select ${fieldLabel}`}
                                      />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="true">
                                        Enabled
                                      </SelectItem>
                                      <SelectItem value="false">
                                        Disabled
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              ) : field === "gcp_private_key" ? (
                                <div className="relative">
                                  <textarea
                                    value={value ?? ""}
                                    onChange={(e) =>
                                      setSectionConfig((prev) => ({
                                        ...prev,
                                        [field]: e.target.value,
                                      }))
                                    }
                                    className="w-96 h-32 p-2 border rounded-md font-mono text-sm"
                                  />
                                </div>
                              ) : field === "aws_region" ? (
                                <div className="flex items-center">
                                  <Select
                                    value={value ?? ""}
                                    onValueChange={(newValue) =>
                                      setSectionConfig((prev) => ({
                                        ...prev,
                                        [field]: newValue,
                                      }))
                                    }
                                  >
                                    <SelectTrigger className="w-96 h-10">
                                      <SelectValue placeholder="Select AWS Region" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {awsRegions.map((region) => (
                                        <SelectItem
                                          key={region.value}
                                          value={region.value}
                                        >
                                          {region.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              ) : (
                                <div className="flex items-center">
                                  {isSensitive ? (
                                    <div className="relative">
                                      <Input
                                        type={
                                          field === "subscription_expiry_days"
                                            ? "number"
                                            : showPasswords[field]
                                            ? "text"
                                            : "password"
                                        }
                                        value={value ?? ""}
                                        onChange={(e) =>
                                          setSectionConfig((prev) => ({
                                            ...prev,
                                            [field]:
                                              field ===
                                              "subscription_expiry_days"
                                                ? parseInt(e.target.value) || ""
                                                : e.target.value,
                                          }))
                                        }
                                        className="pr-10 w-96 h-10"
                                      />
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() =>
                                          togglePasswordVisibility(field)
                                        }
                                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                                      >
                                        {showPasswords[field] ? (
                                          <FiEye className="h-4 w-4" />
                                        ) : (
                                          <FiEyeOff className="h-4 w-4" />
                                        )}
                                        <span className="sr-only">
                                          Toggle password visibility
                                        </span>
                                      </Button>
                                    </div>
                                  ) : (
                                    <Input
                                      type={
                                        field === "subscription_expiry_days"
                                          ? "number"
                                          : "text"
                                      }
                                      value={value ?? ""}
                                      onChange={(e) =>
                                        setSectionConfig((prev) => ({
                                          ...prev,
                                          [field]:
                                            field === "subscription_expiry_days"
                                              ? parseInt(e.target.value) || ""
                                              : e.target.value,
                                        }))
                                      }
                                      className="w-96 h-10"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-end w-full">
                              {field === "auto_ticket_creation" ||
                              field === "slack_events_enabled" ||
                              field === "slack_history_enabled" ? (
                                <div className="w-96 h-10 flex items-center justify-end">
                                  <Badge
                                    variant={value ? "default" : "secondary"}
                                    className="font-medium"
                                  >
                                    {value ? "Enabled" : "Disabled"}
                                  </Badge>
                                </div>
                              ) : field === "aws_region" && value ? (
                                <div className="w-96 h-10 flex items-center justify-end">
                                  <Badge
                                    variant="outline"
                                    className="font-medium"
                                  >
                                    {value}
                                  </Badge>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => {
                                      navigator.clipboard.writeText(value);
                                      showToast("Copied to clipboard", "info");
                                    }}
                                    className="h-6 w-6 ml-1"
                                  >
                                    <FiCopy className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className={`w-96 flex items-center justify-end ${
                                  field === 'gcp_service_account_json' && value && value.length > 150
                                    ? 'min-h-[40px] items-start pt-2'
                                    : 'h-10'
                                }`}>
                                  <span
                                    className={`font-mono text-xs ${
                                      !value || value === ""
                                        ? "text-gray-400 italic"
                                        : "text-gray-600"
                                    } ${
                                      field === 'gcp_service_account_json' && value && value.length > 150
                                        ? 'break-words overflow-hidden line-clamp-3 max-w-full'
                                        : 'truncate'
                                    }`}
                                    title={field === 'gcp_service_account_json' && value && value.length > 150 ? 'Click copy to see full JSON' : undefined}
                                  >
                                    {getDisplayValue(
                                      field,
                                      value,
                                      section.isAiService
                                    )}
                                  </span>
                                  {isSensitive && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() =>
                                        togglePasswordVisibility(field)
                                      }
                                      className="h-6 w-6 ml-1"
                                    >
                                      {showPasswords[field] ? (
                                        <FiEye className="h-4 w-4" />
                                      ) : (
                                        <FiEyeOff className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                  {value && !isEditingSection && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => {
                                        navigator.clipboard.writeText(value);
                                        showToast(
                                          "Copied to clipboard",
                                          "info"
                                        );
                                      }}
                                      className="h-6 w-6 ml-1"
                                    >
                                      <FiCopy className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Bottom action bar for Save/Cancel while editing */}
              {isEditingSection && (
                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-white">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSectionCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSectionSave}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Save
                  </Button>
                </div>
              )}
            </CardContent>
          </div>
        )}
      </Card>
    );
  };

  // Special rendering for Cloud & Storage category: list only accounts from backend
  if (selectedCategory === "cloudStorage") {
    return (
      <div className="space-y-6">

        {/* Create Account Dialog */}
        <Dialog 
          open={showCreateAccountDialog} 
          onOpenChange={(open) => {
            setShowCreateAccountDialog(open);
            if (!open) {
              setCreateAccountState({
                name: "",
                aws_access_key_id: "",
                aws_secret_access_key: "",
                aws_region: "",
                azure_tenant_id: "",
                azure_client_id: "",
                azure_client_secret: "",
                azure_storage_account: "",
                gcp_project_id: "",
                gcp_service_account_json: "",
              });
              setCreateAccountProvider("aws");
            }
          }}
        >
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-gray-900">Add Cloud Storage Account</DialogTitle>
              <DialogDescription className="text-gray-500">
                Configure your cloud provider credentials securely to enable storage scanning.
              </DialogDescription>
            </DialogHeader>
            
            <div className="mt-4">
              <Tabs
                value={createAccountProvider}
                onValueChange={(v) => setCreateAccountProvider(v)}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-3 bg-gray-100 p-1 rounded-lg h-auto gap-2">
                  <TabsTrigger
                    value="aws"
                    className="data-[state=active]:bg-white data-[state=active]:text-[#44087D] data-[state=active]:shadow-sm rounded-md py-2.5 flex items-center gap-2 justify-center font-medium transition-all"
                  >
                    <img
                      src="/settingspage/aws.svg.png"
                      alt="S3"
                      className="w-5 h-5 object-contain"
                    />
                    AWS S3
                  </TabsTrigger>
                  <TabsTrigger
                    value="gcp"
                    className="data-[state=active]:bg-white data-[state=active]:text-[#44087D] data-[state=active]:shadow-sm rounded-md py-2.5 flex items-center gap-2 justify-center font-medium transition-all"
                  >
                    <img
                      src="/inte/GCP.png"
                      alt="GCP"
                      className="w-5 h-5 object-contain"
                    />
                    GCP
                  </TabsTrigger>
                  <TabsTrigger
                    value="azure"
                    className="data-[state=active]:bg-white data-[state=active]:text-[#44087D] data-[state=active]:shadow-sm rounded-md py-2.5 flex items-center gap-2 justify-center font-medium transition-all"
                  >
                    <img
                      src="/inte/Azure.png"
                      alt="Azure"
                      className="w-5 h-5 object-contain"
                    />
                    Azure
                  </TabsTrigger>
                </TabsList>

                {/* AWS Form */}
                <TabsContent value="aws" className="space-y-4 mt-6 focus-visible:outline-none">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Account Name <span className="text-red-500">*</span></Label>
                      <Input
                        placeholder="e.g. Production S3"
                        value={createAccountState.name}
                        onChange={(e) => setCreateAccountState(p => ({ ...p, name: e.target.value }))}
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Access Key ID <span className="text-red-500">*</span></Label>
                      <Input
                        placeholder="AKIA..."
                        value={createAccountState.aws_access_key_id}
                        onChange={(e) => setCreateAccountState(p => ({ ...p, aws_access_key_id: e.target.value }))}
                        className="h-10 font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Secret Access Key <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Input
                          type={showPasswords.aws_secret_access_key_new ? "text" : "password"}
                          placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                          value={createAccountState.aws_secret_access_key}
                          onChange={(e) => setCreateAccountState(p => ({ ...p, aws_secret_access_key: e.target.value }))}
                          className="h-10 font-mono pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, aws_secret_access_key_new: !prev.aws_secret_access_key_new }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.aws_secret_access_key_new ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Region</Label>
                      <Select
                        value={createAccountState.aws_region}
                        onValueChange={(value) => setCreateAccountState(p => ({ ...p, aws_region: value }))}
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="Select a region (optional)" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {awsRegions.map((region) => (
                            <SelectItem key={region.value} value={region.value}>{region.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                {/* GCP Form */}
                <TabsContent value="gcp" className="space-y-4 mt-6 focus-visible:outline-none">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Account Name <span className="text-red-500">*</span></Label>
                      <Input
                        placeholder="e.g. GCP Production"
                        value={createAccountState.name}
                        onChange={(e) => setCreateAccountState(p => ({ ...p, name: e.target.value }))}
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Service Account JSON <span className="text-red-500">*</span></Label>
                      <textarea
                        placeholder='{ "type": "service_account", "project_id": "..." }'
                        value={createAccountState.gcp_service_account_json}
                        onChange={(e) => setCreateAccountState(p => ({ ...p, gcp_service_account_json: e.target.value }))}
                        className="w-full min-h-[200px] p-3 border border-gray-200 rounded-md font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#44087D]/20 focus:border-[#44087D]"
                      />
                      <p className="text-xs text-gray-500">Paste the entire content of your Service Account JSON key file.</p>
                    </div>
                  </div>
                </TabsContent>

                {/* Azure Form */}
                <TabsContent value="azure" className="space-y-4 mt-6 focus-visible:outline-none">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Account Name <span className="text-red-500">*</span></Label>
                      <Input
                        placeholder="e.g. Azure Blob Storage"
                        value={createAccountState.name}
                        onChange={(e) => setCreateAccountState(p => ({ ...p, name: e.target.value }))}
                        className="h-10"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Tenant ID <span className="text-red-500">*</span></Label>
                        <Input
                          value={createAccountState.azure_tenant_id}
                          onChange={(e) => setCreateAccountState(p => ({ ...p, azure_tenant_id: e.target.value }))}
                          className="h-10 font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Client ID <span className="text-red-500">*</span></Label>
                        <Input
                          value={createAccountState.azure_client_id}
                          onChange={(e) => setCreateAccountState(p => ({ ...p, azure_client_id: e.target.value }))}
                          className="h-10 font-mono"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Client Secret <span className="text-red-500">*</span></Label>
                      <div className="relative">
                        <Input
                          type={showPasswords.azure_client_secret_new ? "text" : "password"}
                          value={createAccountState.azure_client_secret}
                          onChange={(e) => setCreateAccountState(p => ({ ...p, azure_client_secret: e.target.value }))}
                          className="h-10 font-mono pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, azure_client_secret_new: !prev.azure_client_secret_new }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.azure_client_secret_new ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Storage Account Name <span className="text-red-500">*</span></Label>
                      <Input
                        value={createAccountState.azure_storage_account}
                        onChange={(e) => setCreateAccountState(p => ({ ...p, azure_storage_account: e.target.value }))}
                        className="h-10 font-mono"
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex items-center justify-end gap-3 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateAccountDialog(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={createStorageAccount}
                  disabled={saving}
                  className="bg-[#44087D] hover:bg-[#360664] text-white min-w-[120px]"
                >
                  {saving ? "Creating..." : "Create Account"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 gap-4">
          {loadingStorageAccounts ? (
            <div className="space-y-4">
              <ShimmerCard />
              <ShimmerCard />
            </div>
          ) : storageAccounts && storageAccounts.length > 0 ? (
            storageAccounts.map((acc) => (
              <Card 
                key={acc.id || acc.account_id || acc.name}
                className="group hover:shadow-md transition-all duration-200 border-gray-200 overflow-hidden"
              >
                <div
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAccountView(acc);
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 group-hover:bg-white group-hover:border-gray-200 transition-colors">
                      <img
                        src={
                          acc.provider && acc.provider.includes("s3")
                            ? "/settingspage/aws.svg.png"
                            : acc.provider === "gcp"
                            ? "/inte/GCP.png"
                            : "/inte/Azure.png"
                        }
                        alt={acc.provider}
                        className="w-6 h-6 object-contain"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{acc.name}</h3>
                        {acc.verified ? (
                          <Badge variant="secondary" className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200 gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Verified
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200 gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Verified
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5 capitalize">
                        {acc.provider === 's3' ? 'Amazon S3' : acc.provider === 'gcp' ? 'Google Cloud Platform' : acc.provider === 'azure' ? 'Azure Storage' : acc.provider}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="border border-[#44087D] bg-[#44087D]/10 text-[#44087D] hover:bg-[#44087D]/20"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenAccountEdit(acc);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        promptDeleteAccount(acc);
                      }}
                    >
                      Delete
                    </Button>
                    <div className={`transform transition-transform duration-200 ${expandedAccountId === (acc.id || acc.account_id) ? "rotate-180" : ""}`}>
                      <FiChevronDown className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedAccountId === (acc.id || acc.account_id) && legacyEditorSection && (
                  <div className="border-t border-gray-100 bg-gray-50/50 p-4">
                    <ServiceCard
                      section={legacyEditorSection}
                      fields={legacyEditorSection.fields}
                      isEditingSection={editingSectionTitle === legacyEditorSection.title}
                      enterEditMode={(title) => setEditingSectionTitle(title)}
                      exitEditMode={() => {
                        setEditingSectionTitle(null);
                        setShowLegacyEditor(false);
                        setEditingAccountId(null);
                        setExpandedAccountId(null);
                      }}
                      forceExpanded={true}
                      hideHeaderActions={true}
                    />
                  </div>
                )}
              </Card>
            ))
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">No Storage Accounts</h3>
              <p className="text-gray-500 mt-1 max-w-sm mx-auto">
                Connect your cloud storage accounts to start scanning for sensitive data.
              </p>
              <Button
                onClick={() => setShowCreateAccountDialog(true)}
                className="mt-6 bg-[#44087D] hover:bg-[#360664] text-white"
              >
                Connect Account
              </Button>
            </div>
          )}
        </div>

        {/* Legacy editor (accordion) for editing an account using existing UI */}
        {showLegacyEditor && legacyEditorSection && (
          <div className="mt-6">
            <ServiceCard
              section={legacyEditorSection}
              fields={legacyEditorSection.fields}
              isEditingSection={
                editingSectionTitle === legacyEditorSection.title
              }
              enterEditMode={(title) => setEditingSectionTitle(title)}
              exitEditMode={() => {
                setEditingSectionTitle(null);
                setShowLegacyEditor(false);
                setEditingAccountId(null);
              }}
            />
          </div>
        )}

        {/* Delete Account Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onOpenChange={(v) => {
            setShowDeleteDialog(v);
            if (!v) setDeleteAccountTarget(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Account</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <DialogDescription>
                Delete a storage account. Make sure no buckets are using this
                account first.
              </DialogDescription>
              <div className="text-sm text-gray-700">
                <div className="font-medium">
                  {deleteAccountTarget?.name || deleteAccountTarget?.id}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={loadingStorageAccounts}
                >
                  Cancel
                </Button>
                <Button
                  onClick={performDeleteAccount}
                  disabled={loadingStorageAccounts}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Provider Selector - Show for categories that have providers (excluding VCS) */}
      {selectedCategory &&
        categoryProviders[selectedCategory] &&
        selectedCategory !== "vcs" && (
          <ProviderSelector
            categoryKey={selectedCategory}
            currentProvider={getCurrentProvider(selectedCategory)}
            onProviderChange={(providerKey) =>
              handleProviderChange(selectedCategory, providerKey)
            }
            vaultAutoInsert={autoInsertToVault}
            onToggleVaultAutoInsert={handleToggleAutoInsertToVault}
          />
        )}

      {/* If viewing Vaults show either provider-specific accounts or a simple
          selector for HashiCorp Vault. The user must click Select to persist
          their choice. */}
      {selectedCategory === "vault" && (
        <div className="mt-4">
          {vaultProvider === "hashicorp" ? (
            <Card className="border shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>HashiCorp Vault</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="py-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-800">HashiCorp Vault</div>
                    <div className="text-xs text-gray-500">Self-hosted HashiCorp Vault</div>
                  </div>
                  <div>
                    {selectedVaultAccount && selectedVaultAccount.vault_pref === "hashicorp" ? (
                      <Button size="sm" variant="outline" disabled>Selected</Button>
                    ) : (
                      <Button
                        size="sm"
                        className="border border-[#44087D] bg-[#44087D]/10 text-[#44087D]"
                        onClick={async (e) => {
                          e.stopPropagation();
                          // create a lightweight acc object for hashicorp
                          const acc = { id: null, name: "HashiCorp Vault" };
                          await selectVaultAccount(acc, "hashicorp");
                        }}
                      >
                        Select
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : vaultProvider === "aws_parameter_store" || vaultProvider === "gcp_secret_manager" ? (
            <Card className="border shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Available Accounts</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {loadingVaultProviderAccounts ? (
                  <div className="space-y-3">
                    <ShimmerCard />
                    <ShimmerCard />
                  </div>
                ) : vaultProviderAccounts && vaultProviderAccounts.length > 0 ? (
                  <div className="space-y-2">
                    {vaultProviderAccounts.map((acc) => (
                      <div
                        key={acc.id || acc.account_id || acc.name}
                        className="flex items-center justify-between gap-4 px-4 py-3 border rounded-lg bg-white w-full"
                      >
                        <div className="flex items-center gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-800 truncate">
                              {acc.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {acc.provider}
                            </div>
                          </div>
                        </div>

                        <div>
                          {selectedVaultAccount &&
                          (selectedVaultAccount.id || selectedVaultAccount.account_id) ===
                            (acc.id || acc.account_id) ? (
                            <Button size="sm" variant="outline" disabled>
                              Selected
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="border border-[#44087D] bg-[#44087D]/10 text-[#44087D]"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await selectVaultAccount(acc, vaultProvider);
                              }}
                            >
                              Select
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 flex items-center justify-center text-sm text-gray-500 italic">
                    No accounts found
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {/* Service Cards */}
      <div className="space-y-6">
        {getFilteredSections().map((section) => (
          <ServiceCard
            key={section.title}
            section={section}
            fields={section.fields}
            isEditingSection={editingSectionTitle === section.title}
            enterEditMode={(title) => setEditingSectionTitle(title)}
            exitEditMode={() => setEditingSectionTitle(null)}
          />
        ))}
        {/* end service cards */}
      </div>
    </div>
  );
};

export default ConfigManagement;
